import { WritableDraft } from 'immer/dist/internal'
import { useMemo } from 'react'

import { EditPortfoliosState } from '../state/edit-portfolio-slice'

interface Permissions {
  areDisallowedStrategiesOverridden: boolean
  canUserSave: boolean
  canUserStartNew: boolean
  isAllowedToViewSelectedPortfolio: boolean
  isUserPortfolioRestricted: boolean
}

export const usePortfolioPermissions = (state: WritableDraft<EditPortfoliosState>): Permissions => {
  const { assetDisallowedStrategies, isNew, portfolios, selectedPortfolio, user } = state
  return useMemo(() => {
    if (!user) {
      return {
        canUserStartNew: false,
        canUserSave: false,
        areDisallowedStrategiesOverridden: false,
        isUserPortfolioRestricted: false,
        isAllowedToViewSelectedPortfolio: undefined
      }
    }

    const portfolioManagerRole = 'PortfolioManager'
    const businessManagerRole = 'BusinessUnitUser'
    const administratorRole = 'Administrator'
    const bankAdminRole = 'BankAdmin'

    const canUserStartNew =
      user.organisation.organisationType === administratorRole ||
      user.roles.includes(administratorRole) ||
      user.roles.includes(bankAdminRole)

    const isPmUser = user.roles[0] === portfolioManagerRole && user.roles?.length === 1
    const isBuPmUser =
      user.roles.includes(businessManagerRole) && user.roles.includes(portfolioManagerRole)
    const isUserPortfolioRestricted = isPmUser || isBuPmUser

    let canUserSave = canUserStartNew
    let areDisallowedStrategiesOverridden = false

    if (selectedPortfolio) {
      canUserSave =
        (canUserSave || user.roles.includes(portfolioManagerRole)) &&
        selectedPortfolio.status !== 'PendingApprovalEdit' &&
        selectedPortfolio.status !== 'PendingApprovalNew'

      areDisallowedStrategiesOverridden =
        selectedPortfolio.disallowedStrategies.some(
          strat => !assetDisallowedStrategies.includes(strat)
        ) || selectedPortfolio.disallowedStrategies.length !== assetDisallowedStrategies.length
    }

    const isAllowedToViewSelectedPortfolio =
      isNew || !selectedPortfolio
        ? true
        : portfolios?.some(p => p.portfolioId === selectedPortfolio.portfolioId) || false

    return {
      canUserStartNew,
      canUserSave,
      areDisallowedStrategiesOverridden,
      isUserPortfolioRestricted,
      isAllowedToViewSelectedPortfolio
    }
  }, [user, selectedPortfolio, portfolios, assetDisallowedStrategies, isNew])
}
